import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { MaskedLines, Eyebrow } from "./motion";
import { useLang } from "../i18n";

const IMAGES = [
  { src: "/img/casas/coracao2/7.jpg", cat: "interior" },
  { src: "/img/casas/marias2/10.jpg", cat: "interior" },
  { src: "/img/casas/coracao2/16.jpg", cat: "interior" },
  { src: "/img/casas/horta2/1.jpg", cat: "interior" },
  { src: "/img/casas/marias2/2.jpg", cat: "interior" },
  { src: "/img/casas/coracao2/19.jpg", cat: "interior" },
  { src: "/img/casas/horta2/5.jpg", cat: "interior" },
  { src: "/img/casas/marias2/13.jpg", cat: "interior" },
  { src: "/img/casas/coracao2/14.jpg", cat: "interior" },
  { src: "/img/casas/coracao2/10.jpg", cat: "exterior" },
  { src: "/img/lago_aereo.jpg", cat: "natureza" },
  { src: "/img/casas/marias2/3.jpg", cat: "interior" },
  { src: "/img/casas/horta2/9.jpg", cat: "exterior" },
  { src: "/img/planicie.jpg", cat: "natureza" },
  { src: "/img/casas/horta2/3.jpg", cat: "interior" },
  { src: "/img/vinha.jpg", cat: "natureza" },
  { src: "/img/azinheira.jpg", cat: "natureza" },
  { src: "/img/casas/marias2/1.jpg", cat: "exterior" },
  { src: "/img/monsaraz.jpg", cat: "exterior" },
  { src: "/img/estrelas.jpg", cat: "natureza" },
];

const FILTERS = ["all", "interior", "exterior", "natureza"];

const Lightbox = ({ list, idx, setIdx }) => {
  const g = useLang().t.gallery;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIdx(null);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % list.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + list.length) % list.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [list.length, setIdx]);
  return (
    <motion.div data-testid="gallery-lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-[#1C1D1A]/95 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setIdx(null)}>
      <AnimatePresence initial={false}>
        <motion.img key={list[idx].src} src={list[idx].src} alt="" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="absolute max-h-[82vh] max-w-[92vw] object-contain rounded-sm" onClick={(e) => e.stopPropagation()} />
      </AnimatePresence>
      <button data-testid="gallery-lightbox-close" className="absolute top-6 right-6 text-[#F7F5F0] p-2" onClick={() => setIdx(null)} aria-label={g.close}><X /></button>
      <button data-testid="gallery-lightbox-prev" className="absolute left-3 md:left-8 text-[#F7F5F0] p-3 rounded-full border border-[#F7F5F0]/40" onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + list.length) % list.length); }} aria-label={g.prev}><ChevronLeft /></button>
      <button data-testid="gallery-lightbox-next" className="absolute right-3 md:right-8 text-[#F7F5F0] p-3 rounded-full border border-[#F7F5F0]/40" onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % list.length); }} aria-label={g.next}><ChevronRight /></button>
    </motion.div>
  );
};

export const Gallery = () => {
  const [filter, setFilter] = useState("all");
  const [idx, setIdx] = useState(null);
  const g = useLang().t.gallery;
  const shuffled = useMemo(() => [...IMAGES].sort(() => Math.random() - 0.5), []);
  const list = filter === "all" ? shuffled : shuffled.filter((i) => i.cat === filter);

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-[#1C1D1A] py-24 md:py-40 text-[#F7F5F0]">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <Eyebrow light>{g.eyebrow}</Eyebrow>
            <MaskedLines lines={[g.l1, <em key="e" className="italic text-[#E8B49A]">{g.l2}</em>]} className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6" />
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((k) => (
              <button key={k} data-testid={`gallery-filter-${k}`} onClick={() => setFilter(k)} className={`btn-pill text-xs border ${filter === k ? "bg-[#F7F5F0] text-[#1C1D1A] border-[#F7F5F0]" : "border-[#F7F5F0]/30 text-[#EFE6DA] hover:border-[#F7F5F0]"}`}>
                {g[k]}
              </button>
            ))}
          </div>
        </div>
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AnimatePresence>
            {list.map((img, i) => (
              <motion.button
                layout
                key={img.src}
                data-testid={`gallery-item-image-${i}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.6 }}
                onClick={() => setIdx(i)}
                className={`group relative overflow-hidden rounded-[20px] aspect-[4/5] ${i % 4 === 1 || i % 4 === 3 ? "lg:translate-y-12" : ""}`}
              >
                <img src={img.src} alt={g[img.cat]} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                <span className="absolute bottom-4 left-4 eyebrow text-[10px] bg-[#1C1D1A]/60 backdrop-blur px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">{g[img.cat]}</span>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
      <AnimatePresence>{idx !== null && list[idx] && <Lightbox list={list} idx={idx} setIdx={setIdx} />}</AnimatePresence>
    </section>
  );
};
